/*
 * The home page's reviews (sections/ktechs-reviews.liquid): the page counter, the arrows and Read
 * more. The row itself is a native scroller, so a swipe, a trackpad and the keyboard move it and
 * can be interrupted at any point; the arrows only ask it to move one view along. Nothing moves by
 * itself (merchant, 28 Sep 2026: kept off after weighing an auto-advance).
 *
 * The counter says where the row is going, not every place it passes: an arrow sets it to the
 * destination at once, and a swipe changes it once the row settles. Read from every scroll frame,
 * "1–3" counted through "2–4" and "3–5" on its way to "4–6" (merchant, 28 Sep: a flicker).
 *
 * A review opens and closes by easing its height, from whatever height it has on screen, so a
 * second press mid-way turns it round without a jump, as the theme's accordion does. The timing is
 * the site's rule for opening in place (ktechs-brand.css): the accordion's 250ms for a press, 400ms
 * for a review shutting because the row turned a page. Reduced motion changes it at once.
 */
if (!customElements.get('ktechs-reviews')) {
  class KtechsReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-reviews-track]');
      if (!this.track) return;
      this.cards = Array.from(this.track.querySelectorAll('[data-review]'));
      this.pager = this.querySelector('[data-reviews-pager]');
      this.positions = Array.from(this.querySelectorAll('[data-reviews-position]'));
      this.steppers = Array.from(this.querySelectorAll('[data-reviews-step]'));
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.frame = 0;
      this.settleTimer = 0;
      this.destination = null;
      this.width = 0;
      this.motions = new Map();

      this.steppers.forEach((button) => {
        button.addEventListener('click', () => this.step(Number(button.dataset.reviewsStep)));
      });
      this.cards.forEach((card) => {
        const more = card.querySelector('[data-review-more]');
        if (more) more.addEventListener('click', () => this.toggle(card, more));
      });
      // scrollend where the browser has it; a short quiet spell after the last scroll otherwise.
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 150);
      }, { passive: true });
      this.track.addEventListener('scrollend', () => this.settle());
      // A new width or text size changes how many lines a review needs, so the row measures again
      // when it resizes. The height also changes on every frame while a review opens or closes;
      // that alone waits, and the review is measured when its motion ends.
      this.resize = new ResizeObserver(() => {
        if (this.track.clientWidth !== this.width || !this.motions.size) {
          this.width = this.track.clientWidth;
          this.measure();
        }
        this.schedule();
      });
      this.resize.observe(this.track);

      // The clamp is measured once the fonts have their real widths, or a line count taken with a
      // fallback font would decide Read more wrongly.
      (document.fonts ? document.fonts.ready : Promise.resolve()).then(() => {
        this.measure();
        this.update();
      });
    }

    disconnectedCallback() {
      if (this.resize) this.resize.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.settleTimer);
    }

    settle() {
      clearTimeout(this.settleTimer);
      this.destination = null;
      const at = this.index();
      this.closeOutside(at, at + this.perView() - 1);
      this.update();
    }

    // A review opened with Read more closes once it leaves the view: the row is as tall as its
    // tallest review, so one left open out of sight kept the whole section tall on the next page
    // (merchant, 28 Sep 2026; the mockup closed them on every page change too).
    closeOutside(first, last) {
      this.cards.forEach((card, i) => {
        if (i >= first && i <= last || !card.classList.contains('is-open')) return;
        const more = card.querySelector('[data-review-more]');
        if (more) this.toggle(card, more, true);
      });
    }

    // The distance from one review to the next: a card plus the gap.
    stride() {
      const [first, second] = this.cards;
      if (!first) return 1;
      return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
    }

    perView() {
      return Math.max(1, Math.round(this.track.clientWidth / this.stride()));
    }

    // The review the row stands on: the one at its start edge.
    index() {
      return Math.round(this.track.scrollLeft / this.stride());
    }

    step(direction) {
      const perView = this.perView();
      const lastStart = Math.max(0, this.cards.length - perView);
      const from = this.destination === null ? this.index() : this.destination;
      const to = Math.min(lastStart, Math.max(0, from + direction * perView));
      this.destination = to;
      this.closeOutside(to, to + perView - 1);
      this.update();
      this.track.scrollTo({
        left: to * this.stride(),
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    update() {
      const total = this.cards.length;
      const perView = this.perView();
      const at = this.destination === null ? this.index() : this.destination;
      const first = Math.min(total, at + 1);
      const last = Math.min(total, first + perView - 1);
      const template = perView > 1 ? this.dataset.positionMany : this.dataset.positionOne;
      const text = (template || '')
        .replace('[first]', first)
        .replace('[last]', last)
        .replace('[total]', total);
      this.positions.forEach((position) => {
        position.textContent = total > perView ? text : '';
      });
      if (this.pager) this.pager.hidden = total <= perView;
      const atStart = this.destination === null ? this.track.scrollLeft <= 1 : at === 0;
      const atEnd = this.destination === null
        ? this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1
        : at >= total - perView;
      this.steppers.forEach((button) => {
        button.disabled = Number(button.dataset.reviewsStep) < 0 ? atStart : atEnd;
      });
    }

    // A review that runs past its space fades and gets Read more; one that fits gets neither. One
    // on its way open or shut is left alone: mid-way its space is not yet the one it will keep.
    measure() {
      this.cards.forEach((card) => {
        if (card.classList.contains('is-open') || this.motions.has(card)) return;
        const text = card.querySelector('[data-review-text]');
        const more = card.querySelector('[data-review-more]');
        if (!text || !more) return;
        const long = text.scrollHeight > text.clientHeight + 1;
        card.classList.toggle('is-long', long);
        more.hidden = !long;
      });
    }

    // following: the review shuts because the row turned a page, not because it was pressed.
    toggle(card, more, following = false) {
      const text = card.querySelector('[data-review-text]');
      const open = !card.classList.contains('is-open');
      const from = text ? text.getBoundingClientRect().height : 0;
      const running = this.motions.get(card);
      if (running) running.cancel();
      card.classList.toggle('is-open', open);
      card.classList.toggle('is-following', following);
      more.setAttribute('aria-expanded', String(open));
      more.textContent = open ? more.dataset.labelLess : more.dataset.labelMore;
      if (!text || this.reduceMotion.matches) return;

      const to = text.getBoundingClientRect().height;
      if (Math.abs(to - from) < 1) return;
      // The height keeps time with the fade at the review's foot: the timing is read from its
      // transition, which ktechs-reviews.css sets from the rule.
      const style = getComputedStyle(text);
      const motion = text.animate({ blockSize: [`${from}px`, `${to}px`] }, {
        duration: parseFloat(style.transitionDuration) * 1000 || 0,
        easing: style.transitionTimingFunction || 'ease'
      });
      this.motions.set(card, motion);
      const done = () => {
        if (this.motions.get(card) === motion) this.motions.delete(card);
      };
      motion.onfinish = () => {
        done();
        this.measure();
      };
      motion.oncancel = done;
    }
  }

  customElements.define('ktechs-reviews', KtechsReviews);
}
